// MARK://dhk8/pos/features/kds/kds-store
// KDS view state: which station this screen shows (persisted in localStorage so it
// survives reload AND new sessions on the same tablet) + a sound toggle. Tickets
// themselves come from react-query (useActiveTickets) — this store does not cache them.
// Absorbed from the former standalone `apps/dhk8-kds/src/stores/kds-store.ts`.
//
// `selected` is the user-visible filter selection:
//   - 'all'           → render every ticket, no station filter sent to backend
//   - 'none'          → only tickets without a stationId (e.g. legacy unrouted)
//   - '<station-uuid>' → only tickets routed to that station

import { create } from 'zustand'

const STATION_KEY = 'dhk8-pos-kds-station'
const SOUND_KEY = 'dhk8_kds_sound'
const ALL = 'all'
const NONE = 'none'

export type KdsSelection = 'all' | 'none' | string

function read(key: string): string | null {
  if (typeof window === 'undefined') return null
  try { return window.localStorage.getItem(key) } catch { return null }
}
function write(key: string, value: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch { /* noop */ }
}

interface KdsState {
  /** Backend-friendly stationId: `null` when selected is 'all' or 'none'.
   *  For 'none' we don't filter at the API (no such param) — UI filters client-side. */
  stationId: string | null
  /** Raw UI selection — drives the dropdown. */
  selected: KdsSelection
  /** `false` until a selection has been made (lets StationPicker run on first boot). */
  stationChosen: boolean
  soundEnabled: boolean
  /** Set the dropdown selection. */
  selectStation: (sel: KdsSelection) => void
  /** Legacy setter — sets a specific UUID or null = 'all'. */
  setStation: (stationId: string | null) => void
  clearStation: () => void
  toggleSound: () => void
}

function selToStationId(sel: KdsSelection): string | null {
  if (sel === ALL || sel === NONE) return null
  return sel
}

export const useKdsStore = create<KdsState>((set) => {
  const stored = read(STATION_KEY)
  const initial: KdsSelection | null = stored === null ? null : (stored === ALL || stored === NONE ? stored : stored)
  return {
    stationId: initial ? selToStationId(initial) : null,
    selected: initial ?? ALL,
    stationChosen: initial !== null,
    soundEnabled: read(SOUND_KEY) !== '0',
    selectStation: (sel) => {
      write(STATION_KEY, sel)
      set({ selected: sel, stationId: selToStationId(sel), stationChosen: true })
    },
    setStation: (stationId) => {
      const sel: KdsSelection = stationId ?? ALL
      write(STATION_KEY, sel)
      set({ stationId, selected: sel, stationChosen: true })
    },
    clearStation: () => {
      write(STATION_KEY, null)
      set({ stationId: null, selected: ALL, stationChosen: false })
    },
    toggleSound: () =>
      set((s) => {
        const next = !s.soundEnabled
        write(SOUND_KEY, next ? '1' : '0')
        return { soundEnabled: next }
      }),
  }
})
